import React from "react"
import styled from "styled-components"

import TiktokIcon from "src/assets/svg/icon-tiktok.svg"
import FacebookIcon from "src/assets/svg/icon-facebook.svg"
import InstagramIcon from "src/assets/svg/icon-instagram.svg"
import TwitterIcon from "src/assets/svg/icon-twitter.svg"

import { useSelector } from "react-redux"
import { selectIsTranscendInitialized } from "src/modules/reducers/transcend"

const getCurrentYear = () => {
  return new Date().getFullYear()
}

const Footer = () => {
  const currentYear = getCurrentYear()

  // Transcend
  const isTranscendInitialized = useSelector(selectIsTranscendInitialized)
  const [showTranscendLink, setShowTranscendLink] = React.useState(false)

  const showTranscendHandler = e => {
    e.preventDefault()
    window.transcend.showConsentManager({
      viewState: "DoNotSellExplainer",
    })
  }

  const hideTranscendHandler = React.useCallback(
    e => {
      if (showTranscendLink && e.key === "Escape") {
        window.transcend.hideConsentManager()
      }
    },
    [showTranscendLink]
  )

  React.useEffect(() => {
    document.addEventListener("keydown", hideTranscendHandler)
    return () => document.removeEventListener("keydown", hideTranscendHandler)
  }, [hideTranscendHandler])

  React.useEffect(() => {
    if (isTranscendInitialized) {
      setShowTranscendLink(
        window.transcend?.ready &&
          window.airgap?.getRegimes &&
          window.airgap.getRegimes().has("DoNotSell")
      )
    }
  }, [isTranscendInitialized])

  return (
    <StyledFooter>
      <FooterMiddle>
        <FooterBlurb>
          <h3>About Fubo Sports Network</h3>
          <p>
            Launched in 2019 and female-founded, Fubo Sports Network is the FAST
            (free ad-supported streaming TV) network featuring thrilling live
            sports, topical shows and award-winning documentaries. Fubo Sports
            Network, owned and operated by live TV streaming company FuboTV Inc.
            (NYSE: FUBO), streams 1,200 hours of live content each year.
            Programming includes partner content from both breakout and niche
            leagues such as Bare Knuckle Fighting Championship (BKFC), The
            Professional Fighters League (PFL) and World Poker Tour, among
            others.
          </p>
          <p>
            Stream for free on Amazon Prime Video, Hulu + Live TV, LG Channels,
            Samsung TV Plus, Sling Freestream, The Roku Channel, VIZIO
            WatchFree+, Tubi, Plex, Xumo Play, TCL Channels, TCL Live TV and
            Tablo TV. Fubo Sports Network is also available as part of DAZN’s
            subscription packages and Fubo’s subscription packages that
            aggregate more than 400 live sports, news and entertainment
            networks. To watch even more programming, follow Fubo Sports Network
            on Instagram, TikTok, X and YouTube.
          </p>
        </FooterBlurb>
      </FooterMiddle>

      <FooterBottom>
        <FooterCopyright>© {currentYear} fuboTV Inc.</FooterCopyright>
        <FooterPrivacy>
          <a
            className="footer-link"
            href="https://www.disneyadvertising.com/contact/"
            target="_blank"
            rel="noopener"
          >
            Advertise
          </a>
          <a className="footer-link" href="mailto:press@fubo.tv">
            Press Inquiries
          </a>
          <a
            className="footer-link"
            href="https://www.fubo.tv/news"
            target="_blank"
            rel="noopener noreferrer"
          >
            News
          </a>
          {showTranscendLink && (
            <a className="footer-link" href="" onClick={showTranscendHandler}>
              Your Privacy Choices
            </a>
          )}
          <a
            className="footer-link"
            href="https://www.fubo.tv/documents/USA/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>

          <a
            className="footer-link"
            href="https://legal.fubo.tv/policies/en-US/?name=unsolicited-submissions-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Unsolicited Submissions
          </a>
          <a
            className="footer-link"
            href="https://www.fubo.tv/documents/USA/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>
          <a
            className="footer-link"
            href="https://www.fubo.tv/documents/USA/cookie-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cookie Policy
          </a>
        </FooterPrivacy>
      </FooterBottom>
    </StyledFooter>
  )
}

export default Footer

const StyledFooter = styled.footer`
  font-size: 14px;
  display: flex;
  padding: 30px;
  flex-direction: column;
  justify-content: center;
  background: #242424;
  font-weight: 800;

  a {
    color: #fff;
  }

  @media (min-width: 576px) {
    justify-content: flex-end;
  }
`

const FooterTopLinks = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;

  @media (max-width: 600px) {
    flex-direction: column-reverse;
    margin-bottom: 20px;
    align-items: center;
  }

  a {
    text-decoration: none;
    margin: 0 5px;

    :first-child {
      margin-left: 0;
    }
  }
`
const FooterNav = styled.nav`
  font-size: 14px;
  font-weight: 800;
  line-height: 2;
  white-space: nowrap;
`
const FooterSocialLinks = styled.div`
  @media (max-width: 600px) {
    margin-bottom: 10px;
  }
  a {
    font-size: 14px;
    font-weight: 800;
    line-height: 2;
    white-space: nowrap;
  }
  svg {
    fill: #fff;
    opacity: 0.5;
  }
`
const FooterMiddle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
`
const FooterBlurb = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.13px;
  line-height: 21px;

  @media (max-width: 600px) {
    max-width: 580px;
    text-align: center;
  }

  h3 {
    color: #fff;
    margin-bottom: 20px;
  }
  p {
    margin-bottom: 20px;
  }
`

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  @media (max-width: 600px) {
    flex-direction: column-reverse;
    align-items: center;
  }
`
const FooterCopyright = styled.div`
  opacity: 0.6;
  color: #fff;
  white-space: nowrap;

  @media (max-width: 600px) {
    margin-top: 10px;
  }
`
const FooterPrivacy = styled.div`
  font-size: 14px;
  font-weight: 800;
  line-height: 2;
  opacity: 0.6;
  gap: 12px;

  a {
    margin-right: 13px;
    text-decoration: none;
    display: inline-block;
  }
`
